import { useEffect, useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { useGameStore } from '@/lib/gameStore';
import { generatePlayerNumbers, getBackgroundState, calculateStressLevel } from '@/lib/gameUtils';
import NumberCard from './NumberCard';
import PlayerAvatar from './PlayerAvatar';
import RoundResults from './RoundResults';
import VoiceActivation from './VoiceActivation';
import ConnectionStatus from './ConnectionStatus';
import QuickChat from './QuickChat';
import LiveGameFeed from './LiveGameFeed';
import WaitingProgress from './WaitingProgress';
import { mapDatabaseError } from '@/lib/errorUtils';
import { useSound } from '@/hooks/useSound';
import { useHaptic } from '@/hooks/useHaptic';
import { useHeartbeat } from '@/hooks/useHeartbeat';
import { Button } from '@/components/ui/button';
import { LogOut, Check, Loader2 } from 'lucide-react';

type MusicIntensity = 'calm' | 'waiting' | 'medium' | 'intense' | 'critical' | 'victory' | 'defeat';
type ConnectionState = 'connected' | 'reconnecting' | 'disconnected';

interface RoundHistoryEntry {
  roundNumber: number;
  sum: number;
  movedCloser: boolean;
}

interface GameRoomProps {
  onGameEnd: () => void;
  onLeave: () => void;
  setMusicIntensity: (intensity: MusicIntensity) => void;
}

export default function GameRoom({ onGameEnd, onLeave, setMusicIntensity }: GameRoomProps) {
  const { currentRoom, playerId, updateRoom } = useGameStore();
  const [myNumbers, setMyNumbers] = useState<[number, number]>([0, 0]);
  const [hasSelected, setHasSelected] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [roundSelections, setRoundSelections] = useState<any[]>([]);
  const [showVoiceActivation, setShowVoiceActivation] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastProcessedRound, setLastProcessedRound] = useState(-1);
  const [connectionState, setConnectionState] = useState<ConnectionState>('connected');
  const [isShaking, setIsShaking] = useState(false);
  const [showFlash, setShowFlash] = useState(false);
  const [playersWhoSelected, setPlayersWhoSelected] = useState<string[]>([]);
  const [roundHistory, setRoundHistory] = useState<RoundHistoryEntry[]>([]);
  const [autoSubmitCountdown, setAutoSubmitCountdown] = useState<number | null>(null);
  const previousTotalRef = useRef<number>(0);
  const autoSubmitTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Use ref to prevent stale closure
  const hasSelectedRef = useRef(false);
  const { playSound } = useSound();
  const { vibrate } = useHaptic();
  const { playerName } = useGameStore();

  // Heartbeat for player activity tracking
  useHeartbeat(playerId, currentRoom?.id || null);

  // Monitor connection status
  useEffect(() => {
    const handleOnline = () => setConnectionState('connected');
    const handleOffline = () => setConnectionState('disconnected');

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const backgroundState = currentRoom ? getBackgroundState(currentRoom.currentTotal, currentRoom.targetTotal) : 'below';

  // Adjust music intensity based on proximity to target
  useEffect(() => {
    if (!currentRoom) return;
    
    if (showVoiceActivation) {
      setMusicIntensity('critical');
      return;
    }

    const distance = Math.abs(currentRoom.currentTotal - 67);
    
    if (distance <= 5) {
      setMusicIntensity('intense');
    } else if (distance <= 15) {
      setMusicIntensity('medium');
    } else {
      setMusicIntensity('waiting');
    }
  }, [currentRoom?.currentTotal, showVoiceActivation, setMusicIntensity]);

  // Keep ref in sync with state
  useEffect(() => {
    hasSelectedRef.current = hasSelected;
  }, [hasSelected]);

  // Verify room status on mount
  useEffect(() => {
    const verifyGameStatus = async () => {
      if (!currentRoom) return;
      
      const { data: room, error } = await supabase
        .from('rooms')
        .select('status')
        .eq('id', currentRoom.id)
        .single();
      
      if (error || room?.status !== 'playing') {
        console.error('Game room accessed but room not in playing state:', room?.status);
        onGameEnd();
      }
    };
    
    verifyGameStatus();
  }, [currentRoom?.id]);

  // Fetch existing round history on mount
  useEffect(() => {
    if (!currentRoom) return;

    const fetchHistory = async () => {
      const { data } = await supabase
        .from('game_history')
        .select('round_number, new_total, previous_total, moved_closer, selections')
        .eq('room_id', currentRoom.id)
        .order('round_number', { ascending: true });

      if (data) {
        setRoundHistory(data.map(h => ({
          roundNumber: h.round_number,
          sum: h.new_total - h.previous_total,
          movedCloser: h.moved_closer ?? false
        })));
      }
    };

    fetchHistory();
  }, [currentRoom?.id]);

  // Subscribe to room updates
  useEffect(() => {
    if (!currentRoom) return;

    const channel = supabase
      .channel('room-updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'rooms',
          filter: `id=eq.${currentRoom.id}`
        },
        (payload) => {
          const newRoom = payload.new as any;
          const newTotal = newRoom.current_total;
          const prevTotal = previousTotalRef.current;
          
          // Check for overshoot - screen shake, flash, and haptic
          if (newTotal > 67 && prevTotal <= 67) {
            setIsShaking(true);
            setShowFlash(true);
            playSound('overshoot');
            vibrate('error');
            setTimeout(() => setIsShaking(false), 600);
            setTimeout(() => setShowFlash(false), 300);
          }
          
          previousTotalRef.current = newTotal;
          
          updateRoom({
            status: newRoom.status,
            currentRound: newRoom.current_round,
            currentTotal: newRoom.current_total,
            targetTotal: newRoom.target_total
          });

          // Check if hit 67
          if (newRoom.current_total === 67 && !showVoiceActivation) {
            setShowVoiceActivation(true);
            vibrate('success');
          }

          // Check if game ended
          if (newRoom.status === 'finished') {
            onGameEnd();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentRoom?.id, showVoiceActivation, playSound, vibrate]);

  // Subscribe to round_selections for real-time "who selected" tracking
  useEffect(() => {
    if (!currentRoom) return;

    // Fetch existing selections for current round
    const fetchCurrentSelections = async () => {
      const { data } = await supabase
        .from('round_selections')
        .select('player_id')
        .eq('room_id', currentRoom.id)
        .eq('round_number', currentRoom.currentRound);
      
      if (data) {
        setPlayersWhoSelected(data.map(s => s.player_id));
      }
    };

    fetchCurrentSelections();

    const channel = supabase
      .channel('round-selections-live')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'round_selections',
          filter: `room_id=eq.${currentRoom.id}`
        },
        (payload) => {
          const selection = payload.new as any;
          if (selection.round_number === currentRoom.currentRound) {
            setPlayersWhoSelected(prev => [...new Set([...prev, selection.player_id])]);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentRoom?.id, currentRoom?.currentRound]);

  // Subscribe to game_history to show results for ALL players and update history
  useEffect(() => {
    if (!currentRoom) return;

    const channel = supabase
      .channel('game-history-updates')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'game_history',
          filter: `room_id=eq.${currentRoom.id}`
        },
        async (payload) => {
          const historyEntry = payload.new as any;
          
          // Update round history immediately from payload (optimistic)
          const sum = historyEntry.new_total - historyEntry.previous_total;
          setRoundHistory(prev => {
            const exists = prev.some(h => h.roundNumber === historyEntry.round_number);
            if (exists) return prev;
            return [...prev, {
              roundNumber: historyEntry.round_number,
              sum,
              movedCloser: historyEntry.moved_closer ?? false
            }];
          });
          
          // Fetch the actual selections for this round
          const { data: selections } = await supabase
            .from('round_selections')
            .select('*')
            .eq('room_id', currentRoom.id)
            .eq('round_number', historyEntry.round_number);
          
          if (selections) {
            setRoundSelections(selections);
            setShowResults(true);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentRoom?.id]);

  // Generate numbers for new round using deterministic seed
  useEffect(() => {
    if (!currentRoom || !playerId) return;
    
    const isOvershoot = currentRoom.currentTotal > currentRoom.targetTotal;
    // Use deterministic seed for server-side validation
    const seed = `${currentRoom.id}-${currentRoom.currentRound}-${playerId}`;
    const numbers = generatePlayerNumbers(
      currentRoom.currentRound, 
      isOvershoot, 
      seed,
      currentRoom.currentTotal,
      currentRoom.targetTotal
    );
    setMyNumbers(numbers);
    setHasSelected(false);
    hasSelectedRef.current = false;
    setIsProcessing(false);
    setPlayersWhoSelected([]); // Reset for new round
    setAutoSubmitCountdown(30); // Reset auto-submit timer to 30 seconds
    
    // Clear any existing timer
    if (autoSubmitTimerRef.current) {
      clearInterval(autoSubmitTimerRef.current);
    }
  }, [currentRoom?.currentRound, playerId]);

  // Auto-submit timer countdown
  useEffect(() => {
    // Don't run timer if results are showing or player already selected
    if (!currentRoom || hasSelected || showResults || autoSubmitCountdown === null || autoSubmitCountdown <= 0) return;
    
    autoSubmitTimerRef.current = setInterval(() => {
      setAutoSubmitCountdown(prev => {
        if (prev === null) return null;
        if (prev <= 1) {
          // Auto-submit with first number
          if (!hasSelectedRef.current && myNumbers[0] !== 0) {
            handleNumberSelect(myNumbers[0]);
            playSound('alert');
            vibrate('warning');
          }
          return null;
        }
        // Warning sounds at 10, 5, 3, 2, 1 seconds
        if (prev === 10 || prev <= 5) {
          playSound('tick', 0.2);
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (autoSubmitTimerRef.current) {
        clearInterval(autoSubmitTimerRef.current);
      }
    };
  }, [currentRoom?.currentRound, hasSelected, showResults, myNumbers, playSound, vibrate]);

  const handleNumberSelect = async (number: number) => {
    if (!currentRoom || !playerId || hasSelected) return;
    
    playSound('select');
    vibrate('selection');

    setHasSelected(true);

    // Store selection
    const { error } = await supabase.from('round_selections').insert({
      room_id: currentRoom.id,
      player_id: playerId,
      round_number: currentRoom.currentRound,
      selected_value: number,
      actual_value: number
    });

    if (error) {
      if (import.meta.env.DEV) {
        console.error('Error storing selection (dev only):', error);
      }
      return;
    }

    // Check if all players have selected
    const { data: selections } = await supabase
      .from('round_selections')
      .select('*')
      .eq('room_id', currentRoom.id)
      .eq('round_number', currentRoom.currentRound);

    const { data: players } = await supabase
      .from('players')
      .select('*')
      .eq('room_id', currentRoom.id)
      .eq('is_active', true);

    // Guard: Only process if all players selected AND we haven't processed this round yet
    if (selections && players && selections.length === players.length && !isProcessing && currentRoom.currentRound !== lastProcessedRound) {
      setIsProcessing(true);
      setLastProcessedRound(currentRoom.currentRound);
      await processRound(selections);
    }
  };

  const processRound = async (selections: any[]) => {
    if (!currentRoom) return;

    try {
      // Call edge function to process round with elevated privileges
      const { data, error } = await supabase.functions.invoke('process-round', {
        body: {
          roomId: currentRoom.id,
          selections: selections.map(s => ({
            player_id: s.player_id,
            selected_value: s.selected_value
          }))
        }
      });

      if (error) {
        console.error('Error processing round:', error);
        setIsProcessing(false);
        return;
      }

      // If round was already processed by another player, wait for realtime update
      if (data.alreadyProcessed) {
        console.log('Round already processed, waiting for realtime update');
        return;
      }

      // Check if voice activation triggered
      if (data.voiceActivation) {
        setShowVoiceActivation(true);
      }
    } catch (error) {
      console.error('Error calling process-round function:', error);
    }
  };

  if (!currentRoom) return null;

  // Check if we're in "close call" territory (within 3 points of 67)
  const distanceToTarget = 67 - currentRoom.currentTotal;
  const isCloseCall = distanceToTarget > 0 && distanceToTarget <= 3;
  const wasCloseCallRef = useRef(false);

  // Get color for total based on proximity
  const getTotalColor = () => {
    const distance = Math.abs(currentRoom.currentTotal - 67);
    if (currentRoom.currentTotal === 67) return 'text-chaos-green';
    if (currentRoom.currentTotal > 67) return 'text-chaos-red';
    if (distance <= 5) return 'text-chaos-orange';
    if (distance <= 15) return 'text-chaos-yellow';
    return 'text-foreground';
  };

  // Get players who haven't selected yet
  const waitingFor = currentRoom.players.filter(p => p.isActive && !playersWhoSelected.includes(p.id));

  // Play tension sound and haptic when entering close call
  useEffect(() => {
    if (isCloseCall && !wasCloseCallRef.current) {
      playSound('closecall', 0.4);
      vibrate('tension');
      wasCloseCallRef.current = true;
    } else if (!isCloseCall) {
      wasCloseCallRef.current = false;
    }
  }, [isCloseCall, playSound, vibrate]);

  // Heartbeat haptic during close call
  useEffect(() => {
    if (!isCloseCall) return;
    
    const interval = setInterval(() => {
      vibrate('heartbeat');
    }, 800);
    
    return () => clearInterval(interval);
  }, [isCloseCall, vibrate]);

  return (
    <>
      <ConnectionStatus state={connectionState} />
      
      {/* Overshoot flash overlay */}
      <AnimatePresence>
        {showFlash && (
          <motion.div
            className="fixed inset-0 z-50 pointer-events-none bg-chaos-red"
            initial={{ opacity: 0.8 }}
            animate={{ opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          />
        )}
      </AnimatePresence>

      {/* Close Call tension overlay */}
      <AnimatePresence>
        {isCloseCall && (
          <>
            {/* Pulsing border effect */}
            <motion.div
              className="fixed inset-0 z-40 pointer-events-none"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className="absolute inset-0 border-[6px] border-chaos-orange rounded-none"
                animate={{
                  boxShadow: [
                    'inset 0 0 30px rgba(255, 149, 0, 0.3), 0 0 30px rgba(255, 149, 0, 0.3)',
                    'inset 0 0 60px rgba(255, 149, 0, 0.5), 0 0 60px rgba(255, 149, 0, 0.5)',
                    'inset 0 0 30px rgba(255, 149, 0, 0.3), 0 0 30px rgba(255, 149, 0, 0.3)',
                  ],
                  borderColor: ['hsl(var(--chaos-orange))', 'hsl(var(--chaos-yellow))', 'hsl(var(--chaos-orange))'],
                }}
                transition={{
                  duration: 0.8,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              />
            </motion.div>

            {/* "CLOSE CALL" text banner */}
            <motion.div
              className="fixed top-20 left-1/2 -translate-x-1/2 z-50 pointer-events-none"
              initial={{ y: -100, opacity: 0, scale: 0.5 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: -100, opacity: 0, scale: 0.5 }}
              transition={{ type: 'spring', damping: 15, stiffness: 300 }}
            >
              <motion.div
                className="px-8 py-3 bg-gradient-to-r from-chaos-orange via-chaos-yellow to-chaos-orange rounded-full"
                animate={{
                  scale: [1, 1.05, 1],
                }}
                transition={{
                  duration: 0.6,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              >
                <span className="text-2xl md:text-3xl font-black text-background tracking-widest uppercase">
                  🔥 {distanceToTarget} AWAY 🔥
                </span>
              </motion.div>
            </motion.div>

            {/* Heartbeat vignette effect */}
            <motion.div
              className="fixed inset-0 z-30 pointer-events-none bg-gradient-radial from-transparent via-transparent to-chaos-orange/20"
              animate={{
                opacity: [0.3, 0.6, 0.3],
              }}
              transition={{
                duration: 0.5,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
          </>
        )}
      </AnimatePresence>

      <div className={`min-h-screen min-h-[100dvh] relative overflow-hidden transition-all duration-500 ${
        backgroundState === 'below' ? 'bg-chaos-red/20' :
        backgroundState === 'above' ? 'bg-chaos-yellow/20' :
        'bg-chaos-green/20'
      } ${isShaking ? 'animate-shake-heavy' : ''}`}>
        {/* Animated background patterns */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-chaos-purple/20 animate-pulse" />
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col h-screen p-4 md:p-8">
          {/* Leave Button */}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onLeave}
            className="absolute top-4 left-4 z-20 text-muted-foreground hover:text-foreground hover:bg-destructive/20"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Leave
          </Button>

          {/* Header - Practice Mode Style */}
          <motion.div 
            className="flex justify-center items-center gap-4 md:gap-8 mb-6 pt-8"
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
          >
            <div className="glass-card px-6 py-3 rounded-2xl text-center min-w-[100px]">
              <div className="text-xs text-muted-foreground font-bold uppercase tracking-wider">Round</div>
              <div className="text-3xl md:text-4xl font-black text-primary">{currentRoom.currentRound}</div>
            </div>

            <motion.div 
              className="glass-card px-8 py-4 rounded-2xl text-center min-w-[140px]"
              animate={{ 
                scale: currentRoom.currentTotal === 67 ? [1, 1.1, 1] : 1,
                boxShadow: currentRoom.currentTotal === 67 ? '0 0 30px hsl(var(--chaos-green))' : 'none'
              }}
              transition={{ duration: 0.5, repeat: currentRoom.currentTotal === 67 ? Infinity : 0 }}
            >
              <div className="text-xs text-muted-foreground font-bold uppercase tracking-wider">Total</div>
              <div className={`text-5xl md:text-6xl font-black ${getTotalColor()} transition-colors`}>
                {currentRoom.currentTotal}
              </div>
            </motion.div>

            <div className="glass-card px-6 py-3 rounded-2xl text-center min-w-[100px]">
              <div className="text-xs text-muted-foreground font-bold uppercase tracking-wider">Target</div>
              <div className="text-3xl md:text-4xl font-black text-chaos-green">{currentRoom.targetTotal}</div>
            </div>
          </motion.div>

          {/* Number Cards OR Results (inline like practice mode) */}
          <div className="flex-1 flex items-center justify-center max-w-4xl mx-auto w-full px-4">
            <AnimatePresence mode="wait">
              {showResults && roundSelections.length > 0 ? (
                <RoundResults 
                  key="results"
                  selections={roundSelections}
                  players={currentRoom.players}
                  previousTotal={currentRoom.currentTotal - roundSelections.reduce((sum, s) => sum + s.selected_value, 0)}
                  newTotal={currentRoom.currentTotal}
                  targetTotal={currentRoom.targetTotal}
                  onClose={() => {
                    setShowResults(false);
                    setHasSelected(false);
                    hasSelectedRef.current = false;
                    setAutoSubmitCountdown(null);
                  }}
                />
              ) : (
                <motion.div 
                  key="cards"
                  className="flex items-center justify-center gap-6 md:gap-12 w-full"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <motion.div 
                    className="flex flex-col items-center gap-2 md:gap-3 flex-1 max-w-xs"
                    initial={{ x: -100, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <div className="text-sm md:text-xl font-black text-muted-foreground uppercase tracking-wider">
                      Option A
                    </div>
                    <NumberCard 
                      number={myNumbers[0]} 
                      onSelect={() => handleNumberSelect(myNumbers[0])}
                      disabled={hasSelected}
                    />
                    {hasSelected && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="flex items-center gap-2 text-primary font-bold text-sm"
                      >
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="hidden sm:inline">Waiting for team...</span>
                        <span className="sm:hidden">Waiting...</span>
                      </motion.div>
                    )}
                  </motion.div>
                  
                  <motion.div 
                    className="flex flex-col items-center gap-2 md:gap-3 flex-1 max-w-xs"
                    initial={{ x: 100, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <div className="text-sm md:text-xl font-black text-muted-foreground uppercase tracking-wider">
                      Option B
                    </div>
                    <NumberCard 
                      number={myNumbers[1]} 
                      onSelect={() => handleNumberSelect(myNumbers[1])}
                      disabled={hasSelected}
                    />
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Players with selection indicators */}
          <motion.div 
            className="flex flex-col items-center gap-3 mb-4"
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
          >
            <div className="flex justify-center gap-4 flex-wrap">
              {currentRoom.players.map((player) => {
                const hasPlayerSelected = playersWhoSelected.includes(player.id);
                return (
                  <div key={player.id} className="flex flex-col items-center gap-1">
                    <div className="relative">
                      <PlayerAvatar player={player} size="md" showStress />
                      {hasPlayerSelected && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-chaos-green flex items-center justify-center"
                        >
                          <Check className="w-4 h-4 text-background" />
                        </motion.div>
                      )}
                      {!hasPlayerSelected && player.isActive && (
                        <motion.div
                          animate={{ opacity: [0.5, 1, 0.5] }}
                          transition={{ duration: 1.5, repeat: Infinity }}
                          className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-muted flex items-center justify-center"
                        >
                          <Loader2 className="w-3 h-3 text-muted-foreground animate-spin" />
                        </motion.div>
                      )}
                    </div>
                    <div className="text-xs font-bold text-foreground">{player.name}</div>
                  </div>
                );
              })}
            </div>
            
            {/* Waiting indicator with progress */}
            {waitingFor.length > 0 && hasSelected && (
              <WaitingProgress 
                submitted={playersWhoSelected.length}
                total={currentRoom.players.filter(p => p.isActive).length}
                autoSubmitTime={autoSubmitCountdown ?? undefined}
              />
            )}
            
            {/* Simple waiting text when not selected yet */}
            {!hasSelected && autoSubmitCountdown !== null && autoSubmitCountdown <= 10 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className={`text-sm font-bold text-center ${autoSubmitCountdown <= 5 ? 'text-chaos-red animate-pulse' : 'text-chaos-orange'}`}
              >
                ⏰ Choose in {autoSubmitCountdown}s or auto-pick!
              </motion.div>
            )}
          </motion.div>

          {/* Round History */}
          {roundHistory.length > 0 && (
            <motion.div 
              className="glass-card px-4 py-3 rounded-xl mx-auto max-w-lg w-full"
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
            >
              <div className="text-xs text-muted-foreground font-bold uppercase tracking-wider mb-2 text-center">
                Round History
              </div>
              <div className="flex flex-wrap justify-center gap-2">
                {roundHistory.map((entry) => (
                  <motion.div
                    key={entry.roundNumber}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className={`px-3 py-1 rounded-full text-sm font-bold ${
                      entry.movedCloser 
                        ? 'bg-chaos-green/20 text-chaos-green' 
                        : 'bg-chaos-red/20 text-chaos-red'
                    }`}
                  >
                    R{entry.roundNumber}: {entry.sum >= 0 ? '+' : ''}{entry.sum}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Quick Chat */}
      {playerId && playerName && (
        <QuickChat 
          roomId={currentRoom.id} 
          playerId={playerId} 
          playerName={playerName} 
        />
      )}

      {/* Live Game Feed */}
      <LiveGameFeed roomId={currentRoom.id} currentRound={currentRoom.currentRound} />

      {/* Results are now shown inline above, no overlay needed */}

      <AnimatePresence>
        {showVoiceActivation && (
          <VoiceActivation 
            roomId={currentRoom.id}
            playerId={playerId!}
            players={currentRoom.players}
            onContinue={() => setShowVoiceActivation(false)}
            onWin={() => {
              supabase.from('rooms').update({ status: 'finished' }).eq('id', currentRoom.id);
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
}
